import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import IndemnityBlock from "./IndemnityBlock";

const slots = [
  { id: "private_30", mins: 30, desc: "Tight focus on one skill" },
  { id: "private_45", mins: 45, desc: "Skill + live drilling" },
  { id: "private_60", mins: 60, desc: "Full camp-style session" },
];

export default function Booking() {
  const [selected, setSelected] = useState("private_60");
  const [coaches, setCoaches] = useState([]);
  const [coachId, setCoachId] = useState("");
  const [availability, setAvailability] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    preferred_date: "",
    preferred_time: "",
    notes: "",
    indemnity_agreed: false,
    indemnity_signature: "",
  });
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/coaches");
        if (cancelled) return;
        const list = res.data || [];
        setCoaches(list);
        if (!coachId && list.length) setCoachId(list[0].id);
      } catch {
        if (!cancelled) setCoaches([]);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  // Fetch availability when coach + (optional) date change
  useEffect(() => {
    if (!coachId) {
      setAvailability(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setAvailabilityLoading(true);
      try {
        const url = form.preferred_date
          ? `/coaches/${coachId}/availability?date=${form.preferred_date}`
          : `/coaches/${coachId}/availability`;
        const res = await api.get(url);
        if (!cancelled) setAvailability(res.data);
      } catch {
        if (!cancelled) setAvailability(null);
      } finally {
        if (!cancelled) setAvailabilityLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [coachId, form.preferred_date]);

  const pkg = slots.find((s) => s.id === selected);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!coachId) {
      toast.error("Please choose a coach.");
      return;
    }
    if (!form.indemnity_agreed) {
      toast.error("You must accept the indemnity waiver.");
      return;
    }
    if (!form.indemnity_signature.trim()) {
      toast.error("Please type your full name as a signature.");
      return;
    }
    if (form.preferred_date && form.preferred_time) {
      const requested = new Date(`${form.preferred_date}T${form.preferred_time}`);
      const hours = (requested.getTime() - Date.now()) / 36e5;
      if (hours < 12) {
        toast.error("Sessions need at least 12 hours' notice. Please pick a later time.");
        return;
      }
    }
    setLoading(true);
    try {
      const res = await api.post("/bookings/request", {
        package_id: selected,
        coach_id: coachId,
        ...form,
      });
      setConfirmation({ ...res.data, preferred_date: form.preferred_date, preferred_time: form.preferred_time });
      setTimeout(() => {
        const el = document.getElementById("booking-confirmation");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Booking failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (confirmation) {
    return (
      <BookingConfirmation
        data={confirmation}
        onReset={() => {
          setConfirmation(null);
          setForm({
            full_name: "",
            email: "",
            phone: "",
            preferred_date: "",
            preferred_time: "",
            notes: "",
            indemnity_agreed: false,
            indemnity_signature: "",
          });
        }}
      />
    );
  }

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="relative py-28 md:py-36 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-16">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-px bg-[#d4af37]" />
            <span className="text-xs uppercase tracking-[0.3em] text-[#d4af37] font-bold">
              Private Session
            </span>
          </div>
          <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl uppercase leading-[0.9]">
            One coach. <br />
            <span className="text-[#d4af37]">One fighter.</span>
          </h2>
          <p className="mt-6 text-white/70 text-base md:text-lg max-w-2xl">
            Book a 1-on-1 with a head coach. Pick your coach and session length,
            request a time, sign the digital waiver. Sessions require{" "}
            <span className="text-[#d4af37] font-semibold">at least
            12 hours' notice</span>. The coach reviews your request and gets
            back to you within 24 hours to confirm the slot and walk you
            through payment privately.
          </p>
        </div>

        {/* Coach selector */}
        {coaches.length > 0 && (
          <div className="mb-10">
            <div className="text-xs uppercase tracking-[0.2em] text-white/60 mb-4">
              Choose Your Coach
            </div>
            <div
              data-testid="booking-coach-grid"
              className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl"
            >
              {coaches.map((c) => {
                const active = coachId === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    data-testid={`coach-${c.id}`}
                    onClick={() => setCoachId(c.id)}
                    className={`flex items-center justify-between p-5 border transition-all ${
                      active
                        ? "bg-[#d4af37] border-[#d4af37] text-black"
                        : "bg-[#0f0f0f] border-white/10 hover:border-[#d4af37]/60 text-white"
                    }`}
                  >
                    <div className="text-left">
                      <div className={`text-[10px] uppercase tracking-[0.25em] ${active ? "text-black/70" : "text-[#d4af37]"}`}>
                        {c.role}
                      </div>
                      <div className="font-heading text-2xl uppercase mt-1">
                        {c.name}
                      </div>
                    </div>
                    <span className={`text-[10px] uppercase tracking-[0.2em] font-bold ${active ? "text-black" : "text-white/40"}`}>
                      {active ? "Selected ✓" : "Pick"}
                    </span>
                  </button>
                );
              })}
            </div>

            {coachId && (
              <CoachAvailabilityPanel
                availability={availability}
                loading={availabilityLoading}
                date={form.preferred_date}
                coachName={coaches.find((c) => c.id === coachId)?.name}
              />
            )}
          </div>
        )}

        {/* Slot selector */}
        <div
          data-testid="booking-slot-grid"
          className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12"
        >
          {slots.map((s) => {
            const active = selected === s.id;
            return (
              <button
                type="button"
                key={s.id}
                data-testid={`slot-${s.id}`}
                onClick={() => setSelected(s.id)}
                className={`text-left p-7 border transition-all ${
                  active
                    ? "bg-[#d4af37] border-[#d4af37] text-black"
                    : "bg-[#0f0f0f] border-white/10 hover:border-[#d4af37]/60 text-white"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-heading text-5xl leading-none">
                      {s.mins}
                      <span className="text-xl ml-1">min</span>
                    </div>
                    <div
                      className={`mt-3 text-xs uppercase tracking-[0.25em] ${
                        active ? "text-black/70" : "text-white/60"
                      }`}
                    >
                      {s.desc}
                    </div>
                  </div>
                </div>
                <div
                  className={`mt-6 text-xs uppercase tracking-[0.25em] font-bold ${
                    active ? "text-black" : "text-[#d4af37]"
                  }`}
                >
                  {active ? "Selected ✓" : "Select"}
                </div>
              </button>
            );
          })}
        </div>

        <form
          onSubmit={onSubmit}
          className="grid grid-cols-1 lg:grid-cols-12 gap-10 bg-[#0a0a0a] border border-white/10 p-8 md:p-10"
          data-testid="booking-form"
        >
          <div className="lg:col-span-7 space-y-8">
            <div>
              <h3 className="font-heading text-2xl md:text-3xl uppercase">Your Details</h3>
              <p className="text-sm text-white/60 mt-2">
                We'll confirm your preferred time within 24 hours.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Full Name" id="bk-name">
                <Input
                  id="bk-name"
                  data-testid="booking-full-name"
                  required
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  className="bg-transparent border-white/15 rounded-none h-12 focus-visible:ring-[#d4af37]"
                />
              </Field>
              <Field label="Email" id="bk-email">
                <Input
                  id="bk-email"
                  type="email"
                  data-testid="booking-email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-transparent border-white/15 rounded-none h-12 focus-visible:ring-[#d4af37]"
                />
              </Field>
              <Field label="Phone" id="bk-phone">
                <Input
                  id="bk-phone"
                  data-testid="booking-phone"
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="bg-transparent border-white/15 rounded-none h-12 focus-visible:ring-[#d4af37]"
                />
              </Field>
              <Field label="Preferred Date" id="bk-date">
                <Input
                  id="bk-date"
                  type="date"
                  data-testid="booking-date"
                  required
                  value={form.preferred_date}
                  onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
                  className="bg-transparent border-white/15 rounded-none h-12 focus-visible:ring-[#d4af37]"
                />
              </Field>
              <Field label="Preferred Time" id="bk-time">
                <Input
                  id="bk-time"
                  type="time"
                  data-testid="booking-time"
                  required
                  value={form.preferred_time}
                  onChange={(e) => setForm({ ...form, preferred_time: e.target.value })}
                  className="bg-transparent border-white/15 rounded-none h-12 focus-visible:ring-[#d4af37]"
                />
              </Field>
            </div>

            <Field label="Goals / Notes" id="bk-notes">
              <Textarea
                id="bk-notes"
                data-testid="booking-notes"
                rows={4}
                placeholder="What do you want to work on? Any injuries we should know about?"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="bg-transparent border-white/15 rounded-none focus-visible:ring-[#d4af37]"
              />
            </Field>

            <IndemnityBlock
              checked={form.indemnity_agreed}
              onCheckedChange={(v) => setForm({ ...form, indemnity_agreed: v })}
              signature={form.indemnity_signature}
              onSignatureChange={(v) => setForm({ ...form, indemnity_signature: v })}
              idPrefix="bk"
            />
          </div>

          {/* Summary */}
          <aside className="lg:col-span-5">
            <div className="bg-[#050505] border border-white/10 p-7 sticky top-24">
              <div className="text-xs uppercase tracking-[0.25em] text-[#d4af37] font-bold">
                Summary
              </div>
              <div className="mt-4 border-t border-white/10 pt-4 space-y-3">
                <Row label="Session">{pkg.mins} minutes</Row>
                <Row label="With">
                  {coaches.find((c) => c.id === coachId)?.name || "Head Coach"}
                </Row>
                <Row label="Location">Full Circle MMA · Unanderra</Row>
                <Row label="Notice">12 hr minimum</Row>
                <Row label="Payment">Arranged with coach</Row>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="booking-submit"
                className="mt-8 w-full inline-flex items-center justify-center px-6 py-4 bg-[#d4af37] text-black text-xs font-bold uppercase tracking-[0.25em] hover:bg-[#f1c40f] transition-colors disabled:opacity-50 btn-lift"
              >
                {loading ? "Sending…" : "Request Booking"}
              </button>
              <div className="mt-4 text-[11px] text-white/50 leading-relaxed">
                Your slot is provisional. The coach reviews your request and
                gets in touch within 24 hours to confirm and arrange payment.
              </div>
            </div>
          </aside>
        </form>
      </div>
    </section>
  );
}

function BookingConfirmation({ data, onReset }) {
  return (
    <section
      id="booking"
      data-testid="booking-confirmation-section"
      className="relative py-28 md:py-36 bg-[#050505]"
    >
      <div id="booking-confirmation" className="max-w-4xl mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-10 h-px bg-[#d4af37]" />
          <span className="text-xs uppercase tracking-[0.3em] text-[#d4af37] font-bold">
            Booking Received
          </span>
        </div>
        <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl uppercase leading-[0.9]">
          Request <span className="text-[#d4af37]">lodged.</span>
        </h2>
        <p className="mt-6 text-white/75 text-base md:text-lg max-w-2xl">
          Cheers — your request to train with{" "}
          <span className="text-[#d4af37] font-semibold">{data.coach_name}</span>{" "}
          is in. The coach will personally reach out within{" "}
          <span className="text-[#d4af37] font-semibold">24 hours</span> on the
          contact details you supplied to confirm your slot and walk you through
          payment.
        </p>

        {/* Booking summary card */}
        <div className="mt-10 bg-[#0a0a0a] border border-white/10 p-7 grid grid-cols-2 md:grid-cols-3 gap-6">
          <DetailCell label="Reference">{data.reference}</DetailCell>
          <DetailCell label="Session">{data.package_label}</DetailCell>
          <DetailCell label="When">
            {data.preferred_date} · {data.preferred_time}
          </DetailCell>
        </div>

        <div className="mt-8 bg-[#0a0a0a] border border-[#d4af37]/30 p-7" data-testid="booking-next-steps">
          <div className="text-xs uppercase tracking-[0.3em] text-[#d4af37] font-bold mb-4">
            What happens next
          </div>
          <ol className="space-y-3 text-sm text-white/80 leading-relaxed list-decimal pl-5">
            <li>
              <span className="text-white font-semibold">{data.coach_name}</span> reviews your request and the time you've asked for.
            </li>
            <li>
              They'll get in touch via the email or phone you provided to lock in the slot.
            </li>
            <li>
              Payment details are arranged privately — the coach will share theirs with you when they confirm.
            </li>
            <li>
              Hold onto your reference <span className="text-[#d4af37] font-semibold">{data.reference}</span> in case you need to follow up.
            </li>
          </ol>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <button
            onClick={onReset}
            data-testid="booking-new"
            className="px-6 py-3 border border-white/20 text-white/80 text-[11px] font-bold uppercase tracking-[0.25em] hover:border-[#d4af37] hover:text-[#d4af37]"
          >
            Book another session
          </button>
          <a
            href="#timetable"
            className="px-6 py-3 bg-[#d4af37] text-black text-[11px] font-bold uppercase tracking-[0.25em] hover:bg-[#f1c40f] text-center"
          >
            View the timetable →
          </a>
        </div>
      </div>
    </section>
  );
}

function DetailCell({ label, children, gold }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-white/50">
        {label}
      </div>
      <div className={`mt-1 font-heading text-lg md:text-xl ${gold ? "text-[#d4af37]" : "text-white"}`}>
        {children}
      </div>
    </div>
  );
}

function Field({ label, id, children }) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs uppercase tracking-[0.2em] text-white/60">
        {label}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function CoachAvailabilityPanel({ availability, loading, date, coachName }) {
  if (loading) {
    return (
      <div
        data-testid="coach-availability"
        className="mt-5 max-w-2xl border border-white/10 bg-[#0a0a0a] p-5 text-xs text-white/50 uppercase tracking-[0.2em]"
      >
        Loading {coachName}'s availability…
      </div>
    );
  }
  if (!availability) return null;
  const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const wh = availability.weekly_hours || {};
  const cb = availability.class_block;
  const dayBlocks = availability.blocks || [];
  const dayHours = availability.day_hours;

  return (
    <div
      data-testid="coach-availability"
      className="mt-5 max-w-2xl border border-[#d4af37]/30 bg-[#0a0a0a] p-5"
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="text-[11px] uppercase tracking-[0.25em] text-[#d4af37] font-bold">
          {coachName}'s Availability
        </div>
        {date && (
          <div className="text-[11px] uppercase tracking-[0.2em] text-white/50">
            For {new Date(date).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })}
          </div>
        )}
      </div>

      <div className="grid grid-cols-7 gap-1 mb-4">
        {dayNames.map((name, idx) => {
          const dh = wh[String(idx)];
          return (
            <div
              key={name}
              className={`text-center py-2 px-1 border ${
                dh ? "border-[#d4af37]/40 bg-[#d4af37]/5" : "border-white/10 bg-black/40"
              }`}
            >
              <div className={`text-[9px] uppercase tracking-[0.15em] font-bold ${dh ? "text-[#d4af37]" : "text-white/30"}`}>
                {name}
              </div>
              <div className={`text-[10px] mt-1 ${dh ? "text-white/85" : "text-white/30"}`}>
                {dh ? `${dh.start}–${dh.end}` : "Off"}
              </div>
            </div>
          );
        })}
      </div>

      {cb && (
        <div className="text-[11px] text-white/60 mb-2 flex items-center gap-2">
          <span className="w-2 h-2 bg-white/30" />
          Open class blocks {cb.start}–{cb.end} every weekday
        </div>
      )}

      {date && (
        <div className="mt-3 pt-3 border-t border-white/10 space-y-1.5 text-[12px]">
          {dayHours ? (
            <div className="text-white/85">
              Working hours that day: <span className="text-[#d4af37] font-semibold">{dayHours.start}–{dayHours.end}</span>
            </div>
          ) : (
            <div className="text-red-400">Coach is off this day — please pick another.</div>
          )}
          {dayBlocks.length > 0 ? (
            <div>
              <div className="text-white/60 mb-1">Already blocked off:</div>
              <ul className="space-y-1">
                {dayBlocks.map((b) => (
                  <li key={b.id} className="text-red-300/80 text-[11px]">
                    {new Date(b.start_at).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}
                    {" – "}
                    {new Date(b.end_at).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}
                    {b.note ? <span className="text-white/40"> · {b.note}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            dayHours && <div className="text-white/50 text-[11px]">No additional blocks on this date.</div>
          )}
        </div>
      )}
      {!date && (
        <div className="mt-3 pt-3 border-t border-white/10 text-[11px] text-white/45">
          Tip: pick a preferred date below to see specific blocked times for that day.
        </div>
      )}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-white/50 uppercase tracking-[0.15em] text-[11px]">
        {label}
      </span>
      <span className="text-white/90">{children}</span>
    </div>
  );
}
